/**
 * whatsappStatus.js — is the CRM's WhatsApp (whatsapp-web.js on the server) connected?
 *
 * One shared poller for the whole app: every component calling useWhatsappStatus()
 * reads the same value, and GET /api/whatsapp/status (the existing getWhatsappStatus
 * action) runs once a minute + when the browser tab regains focus — not once per row.
 *
 * Server states: 'ready' (connected) | 'authenticated' (connecting) | 'qr_ready'
 * (waiting for a QR scan) | 'disconnected' | 'auth_failure'. Before the first answer: null.
 *
 * Exports:
 *   useWhatsappStatus() → { status, phone, connected, label, color }
 *     connected  true only when status === 'ready'
 *     label      Hebrew text for tooltips ("WhatsApp מחובר" …)
 *     color      dot color: green connected, amber connecting/QR, red disconnected, gray unknown
 */
import { useEffect, useState } from 'react';
import { getWhatsappStatus } from '../actions/whatsappActions.js';

const POLL_MS = 60 * 1000;

const STATES = {
  ready: { label: 'WhatsApp מחובר', color: '#16A34A' },
  authenticated: { label: 'WhatsApp מתחבר…', color: '#D97706' },
  qr_ready: { label: 'WhatsApp ממתין לסריקת QR', color: '#D97706' },
  disconnected: { label: 'WhatsApp לא מחובר', color: '#DC2626' },
  auth_failure: { label: 'WhatsApp — שגיאת התחברות', color: '#DC2626' },
};
const UNKNOWN = { label: 'בודק חיבור WhatsApp…', color: '#A1A1AA' };

// ---- shared store ----------------------------------------------------------------
let current = { status: null, phone: null };
const listeners = new Set();
let timer = null;

// The action is a redux-thunk that never dispatches, so it runs with a no-op dispatch.
const refresh = async () => {
  try {
    const data = await getWhatsappStatus()(() => {});
    current = { status: data && data.status ? data.status : 'disconnected', phone: data ? data.phone : null };
  } catch (e) {
    // Not logged in yet / server down → treat as unknown, try again next tick.
    current = { status: null, phone: null };
  }
  listeners.forEach((fn) => fn(current));
};

const onFocus = () => refresh();

const subscribe = (fn) => {
  listeners.add(fn);
  if (listeners.size === 1) {
    refresh();
    timer = setInterval(refresh, POLL_MS);
    window.addEventListener('focus', onFocus);
  }
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0) {
      clearInterval(timer);
      timer = null;
      window.removeEventListener('focus', onFocus);
    }
  };
};

export const useWhatsappStatus = () => {
  const [state, setState] = useState(current);
  useEffect(() => subscribe(setState), []);
  const meta = (state.status && STATES[state.status]) || UNKNOWN;
  return { ...state, connected: state.status === 'ready', label: meta.label, color: meta.color };
};

export default useWhatsappStatus;
